import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, Alert } from '@mui/material';
import api from '../api/axiosConfig';
import { Link as MuiLink } from '@mui/material';
import freeEmailDomains from 'free-email-domains';
import disposableEmailDomains from 'disposable-email-domains';
import Footer from '../components/Footer';

// Google Analytics gtag function and window property typings
const GA_MEASUREMENT_ID = 'G-56R16ZWNK6';
function gtag(...args: any[]) {
  (window as any).dataLayer.push(args);
}
function injectGtag() {
  if (!(window as any).gtagScriptInjected) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID);
    (window as any).gtagScriptInjected = true;
  }
}

const Signup: React.FC = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    injectGtag();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!fullName || !email) {
      setError('All fields are required.');
      return;
    }
    const trimmedEmail = email.trim().toLowerCase();
    const emailParts = trimmedEmail.split('@');
    if (emailParts.length !== 2) {
      setError('Please enter a valid email address.');
      return;
    }
    const emailDomain = emailParts[1];
    if (
      freeEmailDomains.includes(emailDomain) ||
      disposableEmailDomains.includes(emailDomain)
    ) {
      setError('Public email addresses (e.g., Gmail, Yahoo, Outlook, etc.) are not allowed. Please use your corporate email address to sign up.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/sign-up', {
        fullName,
        email,
      });
      setSuccess('Signup successful! Please check your email to verify your account.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      let message = err.response?.data?.ErrorMessage || err.response?.data?.message || 'Signup failed.';
      if (err.message && message === 'Signup failed.') {
        message = err.message;
      }
      // Replace backend message for existing users with a more generic message
      if (message === 'User already exists.') {
        message = 'Invalid email, provide correct details.';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      width="100vw"
      sx={{ background: '#fff' }}
    >
      {/* Main content wrapper */}
      <Box
        display="flex"
        flex={1}
        width="100%"
      >
        {/* Left image section */}
        <Box
          flex={1}
          display={{ xs: 'none', md: 'flex' }}
          alignItems="center"
          justifyContent="center"
          sx={{
            background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)',
            minHeight: 'calc(100vh - 60px)',
            width: '50vw',
            p: 0,
            m: 0,
            overflow: 'hidden',
            position: { md: 'fixed' },
            left: 0,
            top: 0,
            bottom: 60,
            height: 'calc(100vh - 60px)',
            zIndex: 1,
          }}
        >
          <img
            src="/paas.png"
            alt="Prediction as a Service"
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 0, boxShadow: 'none' }}
            onError={e => { (e.target as HTMLImageElement).src = '/logo_black.png'; }}
          />
        </Box>
        {/* Right sign-up section */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={{ xs: '100vw', md: '50vw' }}
          minHeight="calc(100vh - 60px)"
          sx={{ background: '#fff', ml: { md: '50vw' }, zIndex: 2 }}
        >
        <Paper elevation={3} sx={{ p: 6, maxWidth: 500, width: '100%' }}>
          <Box display="flex" justifyContent="center" mb={2}>
            <img src="/logo_black.png" alt="Logo" style={{ height: '50px' }} />
          </Box>
          <Typography variant="h5" mb={2} align="center">Sign Up</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <form onSubmit={handleSubmit}>
            <TextField
              label="Full Name"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              fullWidth
              required
              margin="normal"
              sx={{
                '.MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              required
              margin="normal"
              sx={{
                '.MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={loading}>
              {loading ? 'Signing Up...' : 'Sign Up'}
            </Button>
            <Button variant="outlined" color="primary" fullWidth sx={{ mt: 2 }} onClick={() => alert('Google sign-up coming soon!')}>
              Sign up with Google
            </Button>
            <Box display="flex" justifyContent="center" mt={2}>
              <MuiLink component={Link} to="/login" variant="body2" underline="hover">
                Already have an account? Sign in
              </MuiLink>
            </Box>
          </form>
        </Paper>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default Signup; 